/**
 * Cookie consent store for the Lawis frontend app.
 *
 * Compliant with:
 *  - UAE PDPL (Federal Decree-Law No. 45 of 2021)
 *  - Morocco Loi 09-08 / CNDP
 *
 * Essential storage (auth, subscription, theme, redux-persist, language,
 * this consent record) is always allowed. Analytics/marketing load only
 * after explicit opt-in.
 */

export const CONSENT_VERSION = 1
export const STORAGE_KEY = 'lw_cookie_consent'
export const COOKIE_NAME = 'lawis-consent'
export const CONSENT_EVENT = 'lw:consent-change'

const isBrowser = () => typeof window !== 'undefined'

function writeCookie(name, value, maxAgeSec = 60 * 60 * 24 * 180) {
  if (typeof document === 'undefined') return
  document.cookie = `${name}=${encodeURIComponent(value)};path=/;max-age=${maxAgeSec};samesite=lax`
}

/** @returns {{v:number, ts:string, essential:true, analytics:boolean, marketing:boolean}|null} */
export function getConsent() {
  if (!isBrowser()) return null
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || parsed.v !== CONSENT_VERSION) return null
    return {
      v: CONSENT_VERSION,
      ts: typeof parsed.ts === 'string' ? parsed.ts : '',
      essential: true,
      analytics: !!parsed.analytics,
      marketing: !!parsed.marketing,
    }
  } catch {
    return null
  }
}

export function hasChosenConsent() {
  return getConsent() !== null
}

export function hasAnalyticsConsent() {
  return getConsent()?.analytics === true
}

export function hasMarketingConsent() {
  return getConsent()?.marketing === true
}

export function setConsent({ analytics, marketing }) {
  if (!isBrowser()) return
  const state = {
    v: CONSENT_VERSION,
    ts: new Date().toISOString(),
    essential: true,
    analytics: !!analytics,
    marketing: !!marketing,
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // storage disabled — still dispatch for this session
  }
  const cookieValue = state.analytics || state.marketing ? 'custom' : 'denied'
  writeCookie(COOKIE_NAME, state.analytics && state.marketing ? 'granted' : cookieValue)
  try {
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: state }))
  } catch {
    // old browsers — ignored
  }
}

export function acceptAll() {
  setConsent({ analytics: true, marketing: true })
}

export function rejectAll() {
  setConsent({ analytics: false, marketing: false })
}

export function onConsentChange(cb) {
  if (!isBrowser()) return () => {}
  const handler = (e) => cb(e.detail)
  window.addEventListener(CONSENT_EVENT, handler)
  return () => window.removeEventListener(CONSENT_EVENT, handler)
}

export function openConsentBanner() {
  if (!isBrowser()) return
  window.dispatchEvent(new CustomEvent('lw:consent-open'))
}
